import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Trophy, Zap, TrendingUp, Flame } from 'lucide-react';

interface StyleCardProps {
    title: string;
    description: string;
    iconType: 'shield' | 'trophy' | 'zap' | 'trending' | 'flame';
    stats: Array<{
        label: string;
        value: string | number;
        subtext: string;
        color: string;
    }>;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const StyleCard: React.FC<StyleCardProps> = ({ title, description, iconType, stats }) => {
    const getIcon = () => {
        switch (iconType) {
            case 'trophy': return <Trophy className="w-20 h-20 mb-4 mx-auto text-[#1C7AD6]" />;
            case 'zap': return <Zap className="w-20 h-20 mb-4 mx-auto text-[#1C7AD6]" />;
            case 'trending': return <TrendingUp className="w-20 h-20 mb-4 mx-auto text-[#1C7AD6]" />;
            case 'flame': return <Flame className="w-20 h-20 mb-4 mx-auto text-[#1C7AD6]" />;
            default: return <Shield className="w-20 h-20 mb-4 mx-auto text-[#1C7AD6]" />;
        }
    };

    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-5xl w-full flex flex-col items-center">
                <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, type: "spring" }}
                >
                    <div className="transform scale-125 md:scale-150 mb-8">
                        {getIcon()}
                    </div>
                </motion.div>

                <motion.h2
                    className="text-3xl sm:text-4xl md:text-8xl font-bold mb-4 text-gray-900 tracking-tight px-4 break-words max-w-full"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    {title}
                </motion.h2>

                <motion.p
                    className="text-lg md:text-4xl text-gray-600 mb-12 font-light max-w-3xl px-4 leading-tight"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                >
                    {description}
                </motion.p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 w-full max-w-4xl px-4">
                    {stats.map((stat, index) => (
                        <motion.div
                            key={index}
                            className="bg-white border border-gray-200 rounded-3xl p-6 md:p-8 shadow-lg flex flex-col items-center justify-center transition-transform hover:scale-105 overflow-hidden"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.8 + (index * 0.1) }}
                        >
                            <div className={`text-4xl md:text-6xl font-black mb-2 ${stat.color} break-words max-w-full`}>{stat.value}</div>
                            <div className="text-xs md:text-lg text-gray-700 font-bold uppercase tracking-widest mb-1 text-center">{stat.label}</div>
                            <div className="text-xs md:text-base text-gray-500 text-center">{stat.subtext}</div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default StyleCard;
